/**
 * Format a major-unit amount using the shop's localized currency symbol — i.e.
 * Shopify's currency table via `window.theme.currency_symbol` (set in
 * `layout/theme.liquid` from `localization.country.currency.symbol`).
 *
 * Shared by the PLP product tile (`ProductCard#formatPrice`) and the announcement-bar
 * free-shipping threshold (`formatFreeShippingThreshold.js`) so both surfaces render
 * currency identically (e.g. "Fr157,000", "$75.00", "CAD $75.00").
 *
 * The numeric part comes from `window.formatter` (Intl.NumberFormat, set in
 * `layout/theme.js`); the symbol is overridden with Shopify's so symbol-less
 * currencies render a real symbol (BIF -> "Fr") instead of Intl's ISO-code fallback
 * ("BIF"). The `currencyAlternateIso` prefix — the only hardcoded special-case — is
 * centralized here so a future change touches a single file.
 *
 * @param {Number} amount - amount in major units (e.g. 75 or 446819).
 * @returns {String} formatted price with the symbol at the front.
 */
export default function formatPriceWithSymbol(amount) {
  const currencySymbol = window.theme?.currency_symbol || '$';
  const currencyIso = window.theme?.currency_iso || 'USD';
  // Disambiguate plain "$" dollar currencies (e.g. "CAD $75.00"), excluding USD/HKD.
  const currencyAlternateIso =
    currencySymbol === '$' && currencyIso !== 'USD' && currencyIso !== 'HKD' ? currencyIso : '';

  const numericPart = window.formatter.format(amount).replace(/[^\d.,\s]/g, '').trim();
  const prefix = currencyAlternateIso ? `${currencyAlternateIso} ` : '';

  return `${prefix}${currencySymbol}${numericPart}`;
}
